<template>
  <section class="onboarding-page">
    <div class="onboarding-shell">
      <aside class="progress-panel">
        <p class="eyebrow">Try 10 calls free</p>
        <h1>Create your AI receptionist</h1>
        <p>
          Set up a caller-ready agent in four steps. You can test it immediately
          before activating a dedicated number.
        </p>

        <div class="step-list">
          <button
            v-for="item in steps"
            :key="item.id"
            type="button"
            class="step-item"
            :class="{ active: step === item.id, done: step > item.id }"
            @click="goToStep(item.id)"
          >
            <span>{{ item.id }}</span>
            <strong>{{ item.label }}</strong>
          </button>
        </div>

        <div class="offer-card">
          <strong>Founding customer pricing</strong>
          <span>First 10 calls free, then 100 calls for $250.</span>
        </div>
      </aside>

      <main class="form-panel">
        <form @submit.prevent="submitAgent">
          <div class="form-header">
            <p class="eyebrow">Step {{ step }} of 4</p>
            <h2>{{ currentTitle }}</h2>
            <p>{{ currentDescription }}</p>
          </div>

          <div v-if="step === 1" class="choice-grid">
            <button
              v-for="type in agentTypes"
              :key="type.value"
              type="button"
              class="choice-card"
              :class="{ selected: agent.agent_type === type.value }"
              @click="agent.agent_type = type.value"
            >
              <strong>{{ type.label }}</strong>
              <span>{{ type.description }}</span>
            </button>
          </div>

          <div v-else-if="step === 2" class="choice-grid">
            <button
              v-for="industry in industries"
              :key="industry.value"
              type="button"
              class="choice-card"
              :class="{ selected: agent.industry_key === industry.value }"
              @click="agent.industry_key = industry.value"
            >
              <strong>{{ industry.label }}</strong>
              <span>{{ industry.description }}</span>
            </button>
          </div>

          <div v-else-if="step === 3" class="field-grid">
            <label>
              <span>Business name</span>
              <input v-model.trim="agent.business_name" type="text" placeholder="Example: Northside Dental" required />
            </label>

            <label>
              <span>Agent name</span>
              <input v-model.trim="agent.name" type="text" placeholder="Example: Northside AI Receptionist" required />
            </label>

            <label>
              <span>Business phone</span>
              <input v-model.trim="agent.business_phone" type="tel" placeholder="Example: (438) 555-0101" />
            </label>

            <label>
              <span>Website</span>
              <input v-model.trim="agent.website_url" type="url" placeholder="https://example.com" />
            </label>

            <label class="full">
              <span>What should the AI handle first?</span>
              <textarea
                v-model.trim="agent.primary_goal"
                rows="4"
                placeholder="Example: answer missed calls, collect name and phone, book consultations, and send urgent callers to staff."
                required
              ></textarea>
            </label>

            <label class="full">
              <span>Business hours and handoff rules</span>
              <textarea
                v-model.trim="agent.handoff_rules"
                rows="3"
                placeholder="Example: open Mon-Fri 9-5. If caller is urgent or asks for a person, take a message and notify staff."
              ></textarea>
            </label>
          </div>

          <div v-else class="test-card">
            <div>
              <p class="eyebrow">Ready to test</p>
              <h3>{{ agent.name || 'Your AI receptionist' }}</h3>
              <p>
                We will create the agent, save it as your active agent, and open
                the test-call screen.
              </p>
            </div>

            <ul>
              <li>Captures caller name, phone, service, and urgency</li>
              <li>Summarizes the conversation for your team</li>
              <li>Marks lead status and next action after calls</li>
            </ul>

            <div class="phone-demo-box">
              <strong>Want to hear the live demo now?</strong>
              <a href="tel:+14385339360">Call live demo: (438) 533-9360</a>
              <span>Try saying: “I need an appointment tomorrow afternoon.”</span>
            </div>
          </div>

          <p v-if="error" class="error-message">{{ error }}</p>

          <div class="form-actions">
            <button type="button" class="secondary-button" :disabled="step === 1 || saving" @click="prevStep">
              Back
            </button>

            <button v-if="step < 4" type="button" :disabled="!canContinue" @click="nextStep">
              Continue
            </button>

            <button v-else type="submit" :disabled="saving || !canSubmit">
              {{ saving ? 'Creating...' : 'Create agent and test call now' }}
            </button>
          </div>
        </form>
      </main>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import Cookies from 'js-cookie'
import * as api from '@/services/api'

const router = useRouter()
const step = ref(1)
const saving = ref(false)
const error = ref('')

const steps = [
  { id: 1, label: 'Agent type' },
  { id: 2, label: 'Industry' },
  { id: 3, label: 'Business info' },
  { id: 4, label: 'Test call now' }
]

const agentTypes = [
  {
    value: 'ai_receptionist',
    label: 'AI receptionist',
    description: 'Answers calls, captures leads, books appointments, and hands off to staff.'
  },
  {
    value: 'missed_call_recovery',
    label: 'Missed-call recovery',
    description: 'Focuses on after-hours and busy-line calls so prospects do not go to voicemail.'
  },
  {
    value: 'booking_assistant',
    label: 'Booking assistant',
    description: 'Prioritizes scheduling, confirmations, reminders, and deposit/payment links.'
  }
]

const industries = [
  { value: 'dental_clinic', label: 'Dental clinic', description: 'New patient calls, booking, reminders, and urgent handoff.' },
  { value: 'law_firm', label: 'Law firm', description: 'Intake, lead qualification, consultation booking, and human escalation.' },
  { value: 'real_estate', label: 'Real estate', description: 'Buyer/seller inquiries, showing requests, and rapid follow-up.' },
  { value: 'home_services', label: 'Home services', description: 'Quote requests, emergency calls, booking, and payment links.' },
  { value: 'medical_clinic', label: 'Clinic', description: 'Appointment requests, FAQs, callbacks, and routing.' },
  { value: 'generic', label: 'Other service business', description: 'A simple receptionist flow for local service calls.' }
]

const agent = reactive({
  agent_type: 'ai_receptionist',
  industry_key: 'dental_clinic',
  business_name: '',
  name: '',
  business_phone: '',
  website_url: '',
  primary_goal: '',
  handoff_rules: '',
  default_language: 'english',
  allowed_languages: '["english"]',
  booking_enabled: true,
  active: true
})

const currentTitle = computed(() => {
  if (step.value === 1) return 'Choose what this agent does'
  if (step.value === 2) return 'Choose your industry'
  if (step.value === 3) return 'Add the business details callers expect'
  return 'Create it and place a test call'
})

const currentDescription = computed(() => {
  if (step.value === 1) return 'Keep it focused on one receptionist job first.'
  if (step.value === 2) return 'Industry context helps the AI ask better questions and route calls correctly.'
  if (step.value === 3) return 'These details are enough to test a realistic call in minutes.'
  return 'Your first 10 calls are free. You can activate a dedicated AI number after testing.'
})

const canContinue = computed(() => {
  if (step.value === 1) return Boolean(agent.agent_type)
  if (step.value === 2) return Boolean(agent.industry_key)
  if (step.value === 3) return Boolean(agent.business_name && agent.name && agent.primary_goal)
  return true
})

const canSubmit = computed(() => {
  return Boolean(agent.agent_type && agent.industry_key && agent.business_name && agent.name && agent.primary_goal)
})

function goToStep(targetStep) {
  if (targetStep < step.value || (targetStep === step.value + 1 && canContinue.value)) {
    step.value = targetStep
  }
}

function nextStep() {
  if (step.value < 4 && canContinue.value) step.value += 1
}

function prevStep() {
  if (step.value > 1) step.value -= 1
}

function buildDescription() {
  return [
    `${agent.business_name} AI receptionist.`,
    `Primary goal: ${agent.primary_goal}`,
    agent.handoff_rules ? `Handoff rules: ${agent.handoff_rules}` : '',
    agent.business_phone ? `Business phone: ${agent.business_phone}` : '',
    agent.website_url ? `Website: ${agent.website_url}` : ''
  ].filter(Boolean).join('\n')
}

async function submitAgent() {
  if (!canSubmit.value || saving.value) return

  error.value = ''
  saving.value = true

  try {
    const token = Cookies.get('token')
    if (!token) {
      router.push('/sign-in')
      return
    }

    const response = await api.agents.create({
      ...agent,
      description: buildDescription(),
      voice_style: 'professional_receptionist'
    })

    const agentId = response?.id || response?.agent?.id
    if (agentId) {
      Cookies.set('active_agent_id', agentId)
    }

    router.push('/app/test-live')
  } catch (e) {
    console.error('Agent creation error:', e)
    error.value = e?.message || 'Unable to create your agent. Please try again.'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.onboarding-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 32px 20px;
  color: #0f172a;
}

.onboarding-shell {
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 24px;
}

.progress-panel,
.form-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.06);
}

.progress-panel {
  padding: 28px;
}

.form-panel {
  padding: 32px;
}

.eyebrow {
  margin: 0 0 10px;
  color: #0d8f62;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  font-size: 2.2rem;
  line-height: 1.05;
  letter-spacing: -0.04em;
}

h2 {
  font-size: 2rem;
  letter-spacing: -0.04em;
}

.progress-panel p,
.form-header p,
.choice-card span,
.test-card p,
.test-card li {
  color: #64748b;
  line-height: 1.6;
}

.step-list {
  display: grid;
  gap: 10px;
  margin-top: 28px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  color: #334155;
  text-align: left;
  cursor: pointer;
}

.step-item span {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #f1f5f9;
  font-weight: 800;
}

.step-item.active {
  border-color: #0d8f62;
  background: #f0fdf4;
  color: #064e3b;
}

.step-item.done span {
  background: #0d8f62;
  color: #ffffff;
}

.offer-card {
  display: grid;
  gap: 6px;
  margin-top: 28px;
  padding: 16px;
  border-radius: 14px;
  background: #020617;
  color: #ffffff;
}

.offer-card span {
  color: #cbd5e1;
}

.choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.choice-card {
  min-height: 132px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.choice-card strong {
  display: block;
  margin-bottom: 8px;
  color: #0f172a;
  font-size: 1rem;
}

.choice-card.selected {
  border-color: #0d8f62;
  background: #f0fdf4;
  box-shadow: inset 0 0 0 1px #0d8f62;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

label {
  display: grid;
  gap: 8px;
  font-weight: 800;
  color: #334155;
}

label.full {
  grid-column: 1 / -1;
}

input,
textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 12px 14px;
  color: #0f172a;
  font: inherit;
}

textarea {
  resize: vertical;
}

.test-card {
  display: grid;
  gap: 22px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #f8fafc;
}

.test-card ul {
  margin: 0;
  padding-left: 20px;
}

.phone-demo-box {
  display: grid;
  gap: 8px;
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  background: #f0fdf4;
  padding: 16px;
}

.phone-demo-box a {
  color: #047857;
  font-weight: 900;
  text-decoration: none;
}

.phone-demo-box span {
  color: #475569;
  line-height: 1.5;
}

.error-message {
  margin-top: 18px;
  color: #b91c1c;
  font-weight: 700;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
}

button {
  border: none;
  border-radius: 12px;
  padding: 12px 18px;
  background: #0d8f62;
  color: #ffffff;
  font-weight: 900;
  cursor: pointer;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.secondary-button {
  background: #e2e8f0;
  color: #0f172a;
}

@media (max-width: 900px) {
  .onboarding-shell,
  .choice-grid,
  .field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
